import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import premioBB from 'assets/img/2026/ELBB/premio_bb.png';
import { useAuth } from 'context/AuthContext';
import { buscarIniciativas } from 'services/iniciativa.service';
import { HeaderElbb } from '../HomeElbb/components/Header';
import { HeroIniciativasElbb } from './components/HeroIniciativasElbb';
import { FiltrosIniciativasElbb } from './components/FiltrosIniciativasElbb';
import { IniciativaCardElbb } from '../HomeElbb/components/IniciativaCardElbb';
import './style.scss';

export function IniciativasElbb() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const stateCategoria = (location.state as any)?.categoria;
  const stateFiltroEtapa1 = (location.state as any)?.filtroEtapa1;
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(stateCategoria || 'todos');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [filtroEtapa1, setFiltroEtapa1] = useState(stateFiltroEtapa1 || 'todas');
  const [termoBusca, setTermoBusca] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [iniciativas, setIniciativas] = useState<any[]>([]);
  const [iniciativasFiltradas, setIniciativasFiltradas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const normalizarTexto = (valor?: string) =>
    (valor || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  useEffect(() => {
    async function carregarIniciativas() {
      try {
        setLoading(true);
        const data = await buscarIniciativas();
        // Filtrar apenas ELBB - deve aparecer somente na página IniciativasElbb
        const iniciativasElbb = data.filter(
          (ini: any) => ini.categoryPremio === 'ELBB'
        );
        setIniciativas(iniciativasElbb || []);
      } catch (error) {
        setIniciativas([]);
      } finally {
        setLoading(false);
      }
    }

    if (user?._id) {
      carregarIniciativas();
    }
  }, [user]);

  // Aplicar filtros quando mudar categoria, busca ou iniciativas
  useEffect(() => {
    let resultado = [...iniciativas];

    // Filtrar por perspectiva selecionada no Hero
    if (categoriaSelecionada && categoriaSelecionada !== 'todos') {
      const perspectivaMap: Record<string, string> = {
        clientes: 'clientes',
        financeira: 'financeira',
        asg: 'asg',
        transformacao_digital_processos: 'transformacao digital e processos',
        pessoas_cultura: 'pessoas e cultura',
      };

      const perspectivaSelecionada = perspectivaMap[categoriaSelecionada] || '';

      resultado = resultado.filter((ini) => {
        const perspectivaIniciativa = normalizarTexto(ini.perspectiva || ini.tema);
        return perspectivaIniciativa === perspectivaSelecionada;
      });
    }

    // Filtrar por classificação na Etapa 1
    if (filtroEtapa1 === 'classificadas') {
      resultado = resultado.filter((ini) => ini.classificadaEtapa1 === true);
    } else if (filtroEtapa1 === 'nao-classificadas') {
      resultado = resultado.filter((ini) => !ini.classificadaEtapa1);
    }

    // Filtrar por termo de busca
    if (termoBusca.trim()) {
      const termoLower = termoBusca.toLowerCase();
      resultado = resultado.filter((ini) => {
        return (
          ini.titulo?.toLowerCase().includes(termoLower) ||
          ini.descricao?.toLowerCase().includes(termoLower) ||
          ini.autores?.toLowerCase().includes(termoLower) ||
          ini.perspectiva?.toLowerCase().includes(termoLower) ||
          ini.tema?.toLowerCase().includes(termoLower) ||
          ini.diretoria?.toLowerCase().includes(termoLower)
        );
      });
    }

    setIniciativasFiltradas(resultado);
    setPaginaAtual(1); // Resetar para primeira página ao filtrar
  }, [iniciativas, categoriaFiltro, filtroEtapa1, termoBusca, categoriaSelecionada]);

  const iniciativasPorPagina = 8;
  const totalPaginas = Math.ceil(iniciativasFiltradas.length / iniciativasPorPagina);
  const indexInicio = (paginaAtual - 1) * iniciativasPorPagina;
  const indexFim = indexInicio + iniciativasPorPagina;
  const iniciativasVisiveis = iniciativasFiltradas.slice(indexInicio, indexFim);

  const handleBuscar = () => {
    // A busca já é aplicada via useEffect
  };

  const handlePaginaChange = (pagina: number) => {
    setPaginaAtual(pagina);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div id="iniciativas-elbb-wrapper">
      <HeaderElbb currentPage="iniciativas" />

      <HeroIniciativasElbb
        categoriaSelecionada={categoriaSelecionada}
        onCategoriaChange={setCategoriaSelecionada}
      />

      <FiltrosIniciativasElbb
        categoriaSelecionada={categoriaFiltro}
        onCategoriaChange={setCategoriaFiltro}
        termoBusca={termoBusca}
        onBuscaChange={setTermoBusca}
        onBuscar={handleBuscar}
        filtroEtapa1={filtroEtapa1}
        onFiltroEtapa1Change={setFiltroEtapa1}
      />

      <main className="iniciativas-elbb-main">
        <div className="iniciativas-elbb-main__container">
          <div className="iniciativas-elbb-main__header">
            <h2 className="iniciativas-elbb-main__count" tabIndex={0} aria-live="polite">
              {iniciativasFiltradas.length} iniciativas encontradas
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8" role="status" aria-live="polite">
              <span className="text-primary-elbb">Carregando iniciativas...</span>
            </div>
          ) : iniciativasVisiveis.length === 0 ? (
            <div className="flex flex-col justify-center items-center py-12" role="status">
              <p className="text-lg text-gray-600 mb-2" tabIndex={0}>Ainda não temos iniciativas da ELBB inscritas.</p>
              <p className="text-sm text-gray-500" tabIndex={0}>Volte em breve para conferir as novidades!</p>
            </div>
          ) : (
            <>
              <div className="iniciativas-elbb-main__grid">
                {iniciativasVisiveis.map((iniciativa) => (
                  <IniciativaCardElbb
                    key={iniciativa.id}
                    {...iniciativa}
                  />
                ))}
                {/* Card do Prêmio BB sempre como último card */}
                <aside className="premio-card-elbb" aria-label="Prêmio BB">
                  <div className="premio-card-elbb__inner">
                    <p className="premio-card-elbb__subtitle" tabIndex={0}>Conheça também as</p>
                    <p className="premio-card-elbb__subtitle" tabIndex={0}>iniciativas do</p>
                    <div className="premio-card-elbb__logo">
                      <img src={premioBB} alt="Prêmio BB" tabIndex={0} />
                    </div>
                    <button className="premio-card-elbb__button" onClick={() => window.open('http://premiobb.com.br/', '_blank')} aria-label="Quero conhecer o Prêmio BB">QUERO CONHECER!</button>
                  </div>
                </aside>
              </div>

              {totalPaginas > 1 && (
                <nav className="iniciativas-elbb-main__pagination" role="navigation" aria-label="Paginação de iniciativas">
                  <button
                    className="pagination__btn pagination__btn--prev"
                    onClick={() => handlePaginaChange(paginaAtual - 1)}
                    disabled={paginaAtual === 1}
                    aria-label="Página anterior"
                  >
                    ‹
                  </button>

                  <div className="pagination__numbers">
                    {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((pagina) => (
                      <button
                        key={pagina}
                        className={`pagination__number ${paginaAtual === pagina ? 'active' : ''}`}
                        onClick={() => handlePaginaChange(pagina)}
                        aria-label={`Página ${pagina}`}
                        aria-current={paginaAtual === pagina ? 'page' : undefined}
                      >
                        {pagina}
                      </button>
                    ))}
                  </div>

                  <button
                    className="pagination__btn pagination__btn--next"
                    onClick={() => handlePaginaChange(paginaAtual + 1)}
                    disabled={paginaAtual === totalPaginas}
                    aria-label="Próxima página"
                  >
                    ›
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
